import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Native social-account OAuth ("Connect Instagram", "Connect TikTok", …).
//
// Providers such as Google and TikTok reject embedded webviews, so inside the
// native apps we hand the OAuth URL to the native shell, which presents a
// secure system auth session (ASWebAuthenticationSession on iOS, Custom Tab on
// Android) that shares the login cookie for the round-trip.
//
// On the plain web this component stays out of the way — the link behaves
// exactly as it always has.
export default class extends BridgeComponent {
  static component = "connect";
  static values = { url: String, status: String, message: String };

  connect() {
    super.connect();

    // On the OAuth return page, ask the native shell to dismiss the auth
    // browser and refresh the tab that started the connect.
    if (this.hasStatusValue) {
      this.send("completed", {
        status: this.statusValue,
        message: this.messageValue || "",
      });
    }
  }

  // Wire onto a connect link (the server does this for native UAs via
  // HotwireNativeHelper#native_connect_link_data):
  //   data: { controller: "bridge--connect",
  //           action: "click->bridge--connect#open" }
  //
  // The link's own href (/authorize/:provider) can't be handed to the shell
  // directly: the system auth browser has no session, and /authorize needs
  // one to mint the signed round-trip state. So we fetch it from the webview
  // (session cookie + native UA intact) with native_handoff=1, which returns
  // the provider-bound URL — signed_user_id + native oauth_state included —
  // as JSON, and hand THAT to the shell.
  async open(event) {
    if (!this.isNative) return; // web: follow the link normally

    event.preventDefault();
    // Some legacy connect links are also data-remote; keep rails-ujs out.
    event.stopPropagation();

    const href = this.urlValue || event.currentTarget?.href;
    if (!href) return;

    try {
      const handoffUrl = new URL(href, window.location.origin);
      handoffUrl.searchParams.set("native_handoff", "1");
      const response = await fetch(handoffUrl, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`handoff failed: ${response.status}`);

      const { url } = await response.json();
      if (!url) throw new Error("handoff returned no url");

      this.send("open", { url });
    } catch {
      // Non-OAuth providers (/authorize/* that redirects to an API-key form)
      // and any handoff failure: fall back to normal webview navigation.
      window.location.href = href;
    }
  }

  get isNative() {
    return document.documentElement.hasAttribute("data-hotwire-native");
  }
}
